import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RevenueChart = () => {
  const [chartType, setChartType] = useState('bar');
  const [timeRange, setTimeRange] = useState('7days');

  const revenueData = [
    { name: 'Mon', revenue: 1200, bookings: 8 },
    { name: 'Tue', revenue: 1800, bookings: 12 },
    { name: 'Wed', revenue: 2200, bookings: 15 },
    { name: 'Thu', revenue: 1600, bookings: 10 },
    { name: 'Fri', revenue: 2800, bookings: 18 },
    { name: 'Sat', revenue: 3200, bookings: 22 },
    { name: 'Sun', revenue: 2400, bookings: 16 }
  ];

  const monthlyData = [
    { name: 'Jan', revenue: 45000, bookings: 280 },
    { name: 'Feb', revenue: 52000, bookings: 320 },
    { name: 'Mar', revenue: 48000, bookings: 295 },
    { name: 'Apr', revenue: 61000, bookings: 380 },
    { name: 'May', revenue: 55000, bookings: 340 },
    { name: 'Jun', revenue: 67000, bookings: 420 }
  ];

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '1year', label: 'Last Year' }
  ];

  const getCurrentData = () => {
    switch (timeRange) {
      case '6months': case'1year':
        return monthlyData;
      default:
        return revenueData;
    }
  };

  const formatTooltipValue = (value, name) => {
    if (name === 'revenue') {
      return [`$${value?.toLocaleString()}`, 'Revenue'];
    }
    return [value, 'Bookings'];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-white p-3 border border-border rounded-lg shadow-elevation-2">
          <p className="font-medium text-text-primary">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {formatTooltipValue(entry?.value, entry?.dataKey)?.[1]}: {formatTooltipValue(entry?.value, entry?.dataKey)?.[0]}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-border">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-b border-border space-y-4 sm:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Revenue Analytics</h3>
          <p className="text-sm text-text-secondary">Track business performance over time</p>
        </div>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <Select
            options={timeRangeOptions}
            value={timeRange}
            onChange={setTimeRange}
            className="w-full sm:w-40"
          />
          <div className="flex items-center space-x-2">
            <Button
              variant={chartType === 'bar' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('bar')}
              iconName="BarChart3"
            />
            <Button
              variant={chartType === 'line' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('line')}
              iconName="TrendingUp"
            />
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={getCurrentData()} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="name" 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="revenue" 
                  fill="var(--color-primary)" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            ) : (
              <LineChart data={getCurrentData()} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="name" 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="var(--color-primary)" 
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-6 border-t border-border">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">$14.2K</div>
            <div className="text-sm text-text-secondary">Total Revenue</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">101</div>
            <div className="text-sm text-text-secondary">Total Bookings</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">$141</div>
            <div className="text-sm text-text-secondary">Avg. Order</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1">
              <Icon name="TrendingUp" size={16} color="var(--color-success)" />
              <div className="text-2xl font-bold text-success">+12%</div>
            </div>
            <div className="text-sm text-text-secondary">Growth</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;